//@flow
import * as React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';
import { translate, Trans } from 'react-i18next';

import OverlayDialog from 'common/components/core/dialog/OverlayDialog';
import InputList from 'common/components/core/form/InputList';
import { Button, ButtonTypes } from 'common/components/core/button';
import dialogTheme from './ZendeskDialog.scss';
import Loader from 'common/components/core/loader/Loader';
import Icon from 'common/components/core/icon';
import DialogStatusEnum from 'common/enums/DialogStatusEnum';

import type { InputSourceListType } from 'common/components/core/form/InputList';

type Props = {
    theme: Object,
    status: string,
    error: string,
    uploadError: string,
    active: Object,
    response: Object,
    isFormValid: boolean,
    formInputs: InputSourceListType,
    onFormChange: () => void,
    onDialogClose: () => void,
    onHelpClick: () => void
};

class ZendeskDialog extends React.PureComponent<Props, void> {
    onFormSubmit = () => {
        const { isFormValid, onHelpClick } = this.props;
        if (isFormValid) {
            onHelpClick && onHelpClick();
        }
    };

    formSubmit = e => {
        e.preventDefault();
    };

    onListChange = formData => {
        const { onFormChange } = this.props;
        onFormChange && onFormChange(formData);
    };

    renderDialogInner() {
        const {
            isFormValid,
            formInputs,
            uploadError,
            theme,
            dialogHeader,
            t
        } = this.props;
        return (
            <div className={theme.innerContainer}>
                <div className={theme.header}>{dialogHeader}</div>

                {uploadError ? (
                    <div className={theme.uploadError}>
                        <Trans t={t} i18nKey="uploadError">
                            <span className={theme.semibold}>
                                Upload size exceeds 1 MB,
                            </span>{' '}
                            please remove some files.
                        </Trans>
                    </div>
                ) : null}
                <form
                    method="post"
                    action=""
                    onSubmit={this.formSubmit}
                    className={theme.zendeskForm}
                >
                    <InputList
                        theme={theme}
                        inputs={formInputs}
                        onListChange={this.onListChange}
                    />
                    <Button
                        type="submit"
                        value={t('send')}
                        theme={theme}
                        color="dark-sky-blue"
                        buttonType={ButtonTypes.Solid}
                        uppercase
                        semibold
                        label={t('send')}
                        onClick={this.onFormSubmit}
                        className={theme.submitBtn}
                        disabled={!isFormValid}
                    />
                </form>
            </div>
        );
    }

    renderSuccess = () => {
        const { issueId, theme, t } = this.props;
        return (
            <div className={theme.wrap}>
                <Icon
                    theme={theme}
                    className={theme.successIcon}
                    icon="tickmark"
                />
                <div className={theme.text}>
                    <div>{t('thanksForReachingOut')}</div>
                    <Trans t={t} i18nKey="successRequestSubmitted">
                        Your request (
                        <span className={theme.issueId}>#{{ issueId }}</span>)
                        has been submitted.
                    </Trans>
                </div>
                <div className={theme.text}>
                    <span>{t('successTrackingEmail')}</span>
                    &nbsp;
                </div>
            </div>
        );
    };

    renderError = () => {
        const { error, theme, t } = this.props;
        let errorText = error ? t('tryAgain') : null;
        if (error) {
            if (error.message) {
                errorText = error.message;
            }
        }
        return (
            <div className={theme.innerContainer}>
                <div className={theme.header}>{t('errorLabel')}</div>
                <div className={theme.wrap}>
                    <div className={theme.text}>{errorText}</div>
                </div>
            </div>
        );
    };

    render() {
        const { theme, active, onDialogClose, status, error } = this.props;
        const success = status === DialogStatusEnum.Success;
        const loading = status === DialogStatusEnum.Loading;
        const initial = status === DialogStatusEnum.Initial;
        const statusClass = theme[status.toLowerCase()];
        return (
            <OverlayDialog theme={theme} active={active}>
                <div
                    className={classnames(theme.dialogInner, {
                        [statusClass]: statusClass
                    })}
                >
                    {loading ? <Loader theme={theme} active /> : null}
                    <Button
                        buttonType={ButtonTypes.Icon}
                        circle
                        icon="cancel"
                        className={theme.closeBtn}
                        onClick={onDialogClose}
                        theme={theme}
                    />

                    {success ? this.renderSuccess() : null}
                    {error ? this.renderError() : null}
                    {initial || loading ? this.renderDialogInner() : null}
                </div>
            </OverlayDialog>
        );
    }
}

export default compose(
    themr('ZendeskDialog', dialogTheme),
    translate('zendesk')
)(ZendeskDialog);
